import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import FormActionsFooter from "../FormActionsFooter";
import Personal, { PERSON } from "../InmateDetails/Personal";
import InmateToVisit from "./InmateToVisit";
import { AddVisitorContainer } from "./styles";
import VisitorInformation from "./VisitorInformation";
import { setBookings } from "@/slices/bookingsSlice";

export const MODE = { NEW: "NEW", EDIT: "EDIT", VIEW: "VIEW" };

const AddVisitor = forwardRef(({ mode = MODE.NEW, initialData = null, onHide, isBookings = false }, ref) => {
  const dispatch = useDispatch();
  const { bookings } = useSelector((state) => state.bookings);

  const [personalInformation, setPersonalInformation] = useState({});
  const [visitorInformation, setVisitorInformation] = useState({});
  const [inmateToVisit, setInmateToVisit] = useState({});

  useEffect(() => {
    console.log("Initial Datas : ", initialData);
    if (initialData) {
      const parseToDayjs = (dt) => (dt ? dayjs(dt, "MM/DD/YYYY HH:mm") : null);

      setVisitorInformation({
        scheduledDateTime: parseToDayjs(initialData.scheduledDateTime),
        reasonForVisit: initialData.reasonForVisit || "",
        comments: initialData.comments || "",
        location: initialData.location || "",
        arrivalDateTime: parseToDayjs(initialData.arrivalDateTime),
        departureDateTime: parseToDayjs(initialData.departureDateTime),
        visitorRelationship: initialData?.visitorLogExtra?.[0]?.visitorRelationshipU || "",
      });

     const personalInformation = initialData?.visitorLogInmate?.[0] || {};
     setPersonalInformation({
        fName: personalInformation.inmateFirstName || "",
        lName: personalInformation.inmateLastName || "",
        mName: personalInformation.inmateMiddleName || "",
        suffix: personalInformation. inmateSuffix || "",
      });

    }
  }, [mode, initialData]);

  const handlePersonalInformation = (data) => {
    setPersonalInformation((prev) => ({
      ...prev,
      ...data
    }));
  };

  const handleVisitorInformation = (fieldName, fieldValue) => {
    setVisitorInformation((prev) => ({
      ...prev,
      [fieldName]: fieldValue,
    }));
  };

  const handleInmateToVisit = (e, options = {}) => {
    const { name, value, checked } = e.target;
    const { isCheckBox = false } = options;
    setInmateToVisit((prev) => ({
      ...prev,
      [name]: isCheckBox ? checked : value,
    }));
  };

  const extractDateTime = (dt) => {
    if (!dt) return { date: null, time: null };
    return {
      date: dayjs(dt).format("MM/DD/YYYY"),
      time: dayjs(dt).format("HH:mm"),
    };
  };

  const handleSave = () => {
   const {
      scheduledDateTime,
      arrivalDateTime,
      departureDateTime,
      visitorRelationship,
      ...rest
    } = visitorInformation;

    const { date: sDate, time: sTime } = extractDateTime(scheduledDateTime);
    const { date: aDate, time: aTime } = extractDateTime(arrivalDateTime);
    const { date: dDate, time: dTime } = extractDateTime(departureDateTime);

    const newVisitorLog = {
      ...rest,
      scheduledDate: sDate,
      scheduledTime: sTime,
      arrivalDate: aDate,
      arrivalTime: aTime,
      departureDate: dDate,
      departureTime: dTime,
      visitorLogInmate: [
        {
          inmateFirstName: personalInformation.fName || "",
          inmateLastName: personalInformation.lName || "",   
          inmateMiddleName:  personalInformation.mName || "", 
          inmateSuffix: personalInformation.suffix || "", 

          visitorLogExtra: [
            {
              visitorLogExtraId:
                mode === MODE.EDIT
                  ? initialData?.visitorLogExtra?.[0]?.visitorLogExtraId ?? null
                  : null,
              visitorLogId:
                mode === MODE.EDIT
                  ? initialData?.visitorLogExtra?.[0]?.visitorLogId ?? null
                  : null,
              visitorRelationshipU: visitorRelationship,
            },
          ],
        },
      ],

  
    };

    const existingBookingEntries = bookings?.visitorLogBooking || [];
    let updatedBookingEntries = [...existingBookingEntries];
    if (mode === MODE.NEW) {
     updatedBookingEntries.push({
        visitorLogBookingId: null,
        bookingId: null,
        visitorLogId: null,
        visitorLog: [newVisitorLog],
      });
    } else if (mode === MODE.EDIT) {
      if (initialData?.visitorLogBookingId != null) {
        updatedBookingEntries = updatedBookingEntries.map((entry) => {
          if (entry.visitorLogBookingId === initialData.visitorLogBookingId) {
           return {
              ...entry,
              visitorLog: [newVisitorLog],
            };
          }
          return entry;
        });
      } else {
        console.warn("No visitorLogBookingId found in initialData! Can't edit.");
      }
    }

    const updatedBookings = {
      ...bookings,
      visitorLogBooking: updatedBookingEntries,
    };

   dispatch(setBookings(updatedBookings));

   if (mode === MODE.NEW) {
      setPersonalInformation({});
      setVisitorInformation({});
      setInmateToVisit({});
    }
    onHide();
  };

  useImperativeHandle(ref, () => ({
    save: () => handleSave(),
  }));

  return (
    <AddVisitorContainer>
      {!isBookings &&
        <Personal
          person={PERSON.VISITOR}
          header="Personal Information"
          formData={personalInformation}
          onUpdateForm={handlePersonalInformation}
        />
      }
      <VisitorInformation
        formData={visitorInformation}
        updateFormData={handleVisitorInformation}
        disabled = {isBookings}
      />
      {!isBookings &&
      <InmateToVisit
        formData={inmateToVisit}
        updateFormData={handleInmateToVisit}
      />}

      {!isBookings && mode === MODE.NEW && (
        <FormActionsFooter onSaveClick={handleSave} />
      )}
    </AddVisitorContainer>
  );
});

export default AddVisitor;
